import { useState, useEffect, useCallback } from 'react';
import {
  CheckIcon,
  TrashIcon,
  ClipboardIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { userSession } from '../utils/userSession.js';
import ConfirmDialog from './ConfirmDialog.js';

const API_BASE = 'https://api.driftcal.net';

function formatWhen(value) {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

export default function ApiAccessAdmin() {
  const user = userSession.getUser();
  const adminToken = user?.adminToken;
  const [requests, setRequests] = useState([]);
  const [results, setResults] = useState(null);
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');
  const [searchError, setSearchError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [searching, setSearching] = useState(false);
  const [issued, setIssued] = useState(null);
  const [copied, setCopied] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const adminHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    'x-admin-token': adminToken
  }), [adminToken]);

  const loadRequests = useCallback(async () => {
    if (!adminToken) return;
    setError('');
    try {
      const response = await fetch(`${API_BASE}/v1/admin/requests`, { headers: adminHeaders() });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load API requests');
      }
      setRequests(data.requests || []);
    } catch (err) {
      setError(err.message || 'Failed to load API requests');
    }
  }, [adminToken, adminHeaders]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const runSearch = async (event) => {
    event?.preventDefault();
    const value = query.trim();
    if (value.length < 3) {
      setSearchError('Enter at least 3 characters of an email or origin.');
      setResults(null);
      return;
    }
    setSearching(true);
    setSearchError('');
    try {
      const response = await fetch(
        `${API_BASE}/v1/admin/consumers?q=${encodeURIComponent(value)}`,
        { headers: adminHeaders() }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to search API keys');
      }
      setResults(data.consumers || []);
    } catch (err) {
      setResults(null);
      setSearchError(err.message || 'Failed to search API keys');
    } finally {
      setSearching(false);
    }
  };

  const refreshResults = async () => {
    if (query.trim().length < 3) return;
    await runSearch();
  };

  const handleApprove = async (requestId) => {
    setBusyId(`approve-${requestId}`);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/v1/admin/consumers`, {
        method: 'POST',
        headers: adminHeaders(),
        body: JSON.stringify({ requestId })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to approve request');
      }
      setIssued(data);
      setCopied(false);
      await loadRequests();
    } catch (err) {
      setError(err.message || 'Failed to approve request');
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (requestId) => {
    setBusyId(`reject-${requestId}`);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/v1/admin/requests/${requestId}/reject`, {
        method: 'POST',
        headers: adminHeaders()
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to reject request');
      }
      await loadRequests();
    } catch (err) {
      setError(err.message || 'Failed to reject request');
    } finally {
      setBusyId(null);
    }
  };

  const handleRotate = async (consumerId) => {
    setBusyId(`rotate-${consumerId}`);
    setSearchError('');
    try {
      const response = await fetch(`${API_BASE}/v1/admin/consumers/${consumerId}/rotate`, {
        method: 'POST',
        headers: adminHeaders()
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to rotate key');
      }
      setIssued(data);
      setCopied(false);
      await refreshResults();
    } catch (err) {
      setSearchError(err.message || 'Failed to rotate key');
    } finally {
      setBusyId(null);
      setConfirm(null);
    }
  };

  const handleRevoke = async (consumerId) => {
    setBusyId(`revoke-${consumerId}`);
    setSearchError('');
    try {
      const response = await fetch(`${API_BASE}/v1/admin/consumers/${consumerId}/revoke`, {
        method: 'POST',
        headers: adminHeaders()
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to revoke key');
      }
      if (issued?.consumerId === consumerId) {
        setIssued(null);
      }
      await refreshResults();
    } catch (err) {
      setSearchError(err.message || 'Failed to revoke key');
    } finally {
      setBusyId(null);
      setConfirm(null);
    }
  };

  const copyKey = async () => {
    if (!issued?.apiKey) return;
    try {
      await navigator.clipboard.writeText(issued.apiKey);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  if (!adminToken) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 shadow-sm mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">API Requests</h2>
        <p className="text-sm text-amber-900">
          Sign out and sign in again to manage API keys. Admin access is tied to your current session.
        </p>
      </div>
    );
  }

  const pending = requests.filter((row) => row.status === 'pending');

  return (
    <div className="space-y-8 mb-8">
      {issued?.apiKey && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 shadow-sm">
          <h2 className="text-lg font-semibold text-amber-950">
            {issued.rotated ? 'Key rotated — copy it now' : 'Key issued — copy it now'}
          </h2>
          <p className="mt-1 text-sm text-amber-900">
            This is the only time it is shown here. It was also emailed to {issued.email}.
          </p>
          <p className="mt-2 text-xs text-amber-800">
            Origin: {issued.origin}
          </p>
          <div className="mt-3 flex items-start gap-2">
            <code className="flex-1 break-all rounded-md bg-white px-3 py-2 text-xs text-slate-900 border border-amber-200">
              {issued.apiKey}
            </code>
            <button
              type="button"
              onClick={copyKey}
              className="shrink-0 inline-flex items-center gap-1 rounded-md bg-slate-900 px-3 py-2 text-xs font-medium text-white hover:bg-slate-700"
            >
              {copied ? <CheckCircleIcon className="h-4 w-4" /> : <ClipboardIcon className="h-4 w-4" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border rounded-lg p-4 shadow-sm">
        <h2 className="text-xl font-semibold text-gray-900">API Requests</h2>
        <p className="mb-4 text-sm text-gray-600">
          Approve issues a key for that origin. Reject drops it from the queue.
        </p>
        {error && (
          <div className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        )}
        {pending.length === 0 ? (
          <p className="text-sm text-gray-500">0 pending</p>
        ) : (
          <div className="space-y-4">
            {pending.map((request) => (
              <div key={request.id} className="flex flex-col p-4 border rounded-lg hover:bg-gray-50">
                <div className="mb-4 space-y-1">
                  <div className="font-bold text-gray-900">{request.name}</div>
                  <div className="text-sm text-gray-800">{request.email}</div>
                  {request.organisation && (
                    <div className="text-sm italic text-gray-600">{request.organisation}</div>
                  )}
                  <div className="text-sm font-mono text-gray-700">{request.origin}</div>
                  {request.use_case && (
                    <div className="text-sm text-gray-600">{request.use_case}</div>
                  )}
                </div>
                <div className="flex justify-between border-t pt-4">
                  <button
                    type="button"
                    disabled={Boolean(busyId)}
                    onClick={() => handleReject(request.id)}
                    className="flex items-center text-sm space-x-1 px-4 py-0 text-red-600 hover:text-red-800 rounded-full hover:bg-red-100 disabled:opacity-50"
                  >
                    <TrashIcon className="h-5 w-5" />
                    <span>{busyId === `reject-${request.id}` ? 'Rejecting…' : 'Reject'}</span>
                  </button>
                  <button
                    type="button"
                    disabled={Boolean(busyId)}
                    onClick={() => handleApprove(request.id)}
                    className="flex items-center text-sm space-x-1 px-4 py-0 text-green-600 hover:text-green-800 rounded-full hover:bg-green-100 disabled:opacity-50"
                  >
                    <CheckIcon className="h-5 w-5" />
                    <span>{busyId === `approve-${request.id}` ? 'Approving…' : 'Approve'}</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border rounded-lg p-4 shadow-sm">
        <h2 className="text-xl font-semibold text-gray-900">API Search</h2>
        <p className="mb-4 text-sm text-gray-600">
          Find a key by email or origin. Rotate emails a new secret and invalidates the old one.
        </p>
        <form onSubmit={runSearch} className="mb-4 flex gap-2">
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="email or https://…"
            className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
          />
          <button
            type="submit"
            disabled={searching || Boolean(busyId)}
            className="inline-flex items-center gap-1 rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
          >
            <MagnifyingGlassIcon className="h-4 w-4" />
            {searching ? 'Searching…' : 'Search'}
          </button>
        </form>
        {searchError && (
          <div className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{searchError}</div>
        )}
        {results === null ? (
          <p className="text-sm text-gray-500">Search to see a key.</p>
        ) : results.length === 0 ? (
          <p className="text-sm text-gray-500">No keys match that email or origin.</p>
        ) : (
          <div className="space-y-3">
            {results.map((consumer) => {
              const active = consumer.status === 'active';
              return (
                <div
                  key={consumer.id}
                  className={`flex flex-col p-4 border rounded-lg ${active ? '' : 'bg-gray-50 opacity-80'}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1 min-w-0">
                      <div className={`font-bold ${active ? 'text-gray-900' : 'text-gray-700'}`}>
                        {consumer.name}
                      </div>
                      <div className="text-sm text-gray-800">{consumer.email}</div>
                      {consumer.organisation && (
                        <div className="text-sm italic text-gray-600">{consumer.organisation}</div>
                      )}
                      <div className="break-all font-mono text-sm text-gray-800">{consumer.key_prefix}</div>
                      <div className="text-xs text-gray-500">
                        {consumer.origin} · last used {formatWhen(consumer.last_used_at)}
                      </div>
                    </div>
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
                        active
                          ? 'bg-emerald-50 text-emerald-800'
                          : 'bg-gray-200 text-gray-700'
                      }`}
                    >
                      {consumer.status}
                    </span>
                  </div>
                  {active && (
                    <div className="mt-3 flex justify-end gap-2 border-t pt-3">
                      <button
                        type="button"
                        disabled={Boolean(busyId)}
                        onClick={() => setConfirm({ type: 'rotate', consumer })}
                        className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                      >
                        <ArrowPathIcon className="h-4 w-4" />
                        {busyId === `rotate-${consumer.id}` ? 'Rotating…' : 'Rotate'}
                      </button>
                      <button
                        type="button"
                        disabled={Boolean(busyId)}
                        onClick={() => setConfirm({ type: 'revoke', consumer })}
                        className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                      >
                        <TrashIcon className="h-4 w-4" />
                        {busyId === `revoke-${consumer.id}` ? 'Revoking…' : 'Revoke'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          if (!confirm) return;
          if (confirm.type === 'rotate') handleRotate(confirm.consumer.id);
          if (confirm.type === 'revoke') handleRevoke(confirm.consumer.id);
        }}
        title={confirm?.type === 'rotate' ? 'Rotate API key' : 'Revoke API key'}
        message={
          confirm?.type === 'rotate'
            ? `Replace the key for ${confirm.consumer.email} (${confirm.consumer.origin})? The current key will stop working immediately. The new key is shown once and emailed.`
            : confirm
              ? `Revoke the key for ${confirm.consumer.email} (${confirm.consumer.origin})? Calls with that key will start failing immediately.`
              : ''
        }
        confirmLabel={confirm?.type === 'rotate' ? 'Rotate' : 'Revoke'}
      />
    </div>
  );
}
